"use client";

import { useState } from "react";
import {
  Notice,
  SelectField,
  TextAreaField,
} from "@/components/tool-kit/primitives";
import {
  alphaOptions,
  ExampleButton,
} from "@/components/tools/stats-tests/kit";
import { ShapiroWilkResults } from "@/components/tools/stats-tests/shapiro-wilk-results";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import { parseNumberList } from "@/lib/parse-number";
import {
  MAX_N,
  MIN_N,
  shapiroWilk,
} from "@/lib/tool-kit/stats-tests/shapiro-wilk";

/** English strings. A translated content file overrides these through `ui`. */
export const SHAPIRO_WILK_TEST_CALCULATOR_STRINGS = {
  dataLabel: "Your data — numbers separated by commas, spaces, or new lines",
  dataPlaceholder: "148, 154, 158, 160, 161",
  valuesDetected: "one: value|other: values",
  alphaLabel: "Significance level (α)",
  tryExample: "Try an example",
  exampleData: "148, 154, 158, 160, 161, 162, 166, 170, 182, 195, 236",
  results: "Results",
  wStatistic: "W statistic",
  pValue: "p-value",
  n: "Sample size (n)",
  skewness: "Skewness (G₁)",
  kurtosis: "Excess kurtosis (G₂)",
  mean: "Mean",
  sd: "Standard deviation",
  decisionReject:
    "Reject normality at α = {alpha}: p = {p} is below α. The data depart from a normal distribution — look at the Q–Q plot to see how.",
  decisionKeep:
    "No significant departure from normality at α = {alpha} (p = {p}). That does not prove the data are normal; it means this test could not detect a departure with this sample.",
  methodExact: "For n = 3 the p-value is exact.",
  methodSmall:
    "For 4 ≤ n ≤ 11 the p-value comes from Royston's (1995) normalising transformation for small samples.",
  methodLarge:
    "For n ≥ 12 the p-value comes from Royston's (1995) normal approximation to log(1 − W).",
  largeSample:
    "With {n} values the test can flag departures too small to matter. Judge normality from the Q–Q plot and from what your analysis can tolerate, not from p alone.",
  smallSample:
    "With {n} values the test has little power: even clearly non-normal data often pass. Treat a non-significant result with caution.",
  shapeHint:
    "Skewness and excess kurtosis are the bias-adjusted G₁ and G₂ that SPSS and Excel report; both are 0 for a normal distribution.",
  plotHeading: "Normal Q–Q plot",
  plotHint:
    "Each point is an ordered value plotted against the normal score it would have in a perfectly normal sample, z = Φ⁻¹((i − 3/8)/(n + 1/4)). The dashed line runs through the first and third quartiles, as R's qqline does, so a single outlier cannot drag it.",
  plotLabel: "Normal Q–Q plot of {n} values",
  xTitle: "Theoretical normal quantile (z)",
  yTitle: "Sample value",
  downloadPlot: "Download plot (SVG)",
  howCalculated: "How this was calculated",
  workingW:
    "W = (Σ aᵢ(x₍ₙ₊₁₋ᵢ₎ − x₍ᵢ₎))² / Σ(x − x̄)² = {b}² / {ss} = {w}, with the coefficients aᵢ from Royston's algorithm AS R94.",
  workingP: "{method} p = {p}.",
  apaHeading: "APA-style result",
  apaTemplate: "*W*({n}) = {w}, *p* {p}",
  apaNote:
    "Report the test alongside a Q–Q plot or a histogram; a normality test on its own says little about whether the departure matters.",
  copyApa: "Copy APA result",
  copied: "Copied!",
  prompt: "Enter at least {min} numbers.",
  tooMany:
    "The Shapiro-Wilk test is calibrated for up to {max} values; you entered {n}.",
  noRange:
    "Every value is identical, so the data have no spread and W is undefined.",
} as const;

/** Above this n, the test detects trivial departures; below it, it misses large ones. */
const LARGE_N = 100;
const SMALL_N = 10;

export default function ShapiroWilkTestCalculatorWidget() {
  const strings = useToolUi(SHAPIRO_WILK_TEST_CALCULATOR_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [raw, setRaw] = useState("");
  const [alpha, setAlpha] = useState("0.05");

  const values = parseNumberList(raw);
  const outcome = values.length > 0 ? shapiroWilk(values) : null;
  let notice: string = t(strings.prompt, { min: MIN_N });
  if (outcome && !outcome.ok && outcome.reason === "too-many") {
    notice = t(strings.tooMany, { max: fmt(MAX_N), n: fmt(values.length) });
  } else if (outcome && !outcome.ok && outcome.reason === "no-range") {
    notice = strings.noRange;
  }

  return (
    <div className="space-y-6">
      <TextAreaField
        hint={`${fmt(values.length)}${tPlural(strings.valuesDetected, locale, values.length)}`}
        id="sw-data"
        label={strings.dataLabel}
        onChange={setRaw}
        placeholder={strings.dataPlaceholder}
        value={raw}
      />
      <div className="flex flex-wrap items-end gap-4">
        <div className="w-full max-w-xs">
          <SelectField
            id="sw-alpha"
            label={strings.alphaLabel}
            onChange={setAlpha}
            options={alphaOptions(fmt)}
            value={alpha}
          />
        </div>
        <ExampleButton
          label={strings.tryExample}
          onClick={() => setRaw(strings.exampleData)}
        />
      </div>
      {outcome?.ok ? (
        <ShapiroWilkResults
          alpha={Number(alpha)}
          fmt={fmt}
          large={values.length > LARGE_N}
          r={outcome.result}
          small={values.length < SMALL_N}
          strings={strings}
          values={values}
        />
      ) : (
        <Notice>{notice}</Notice>
      )}
    </div>
  );
}
